import { bustBodyBox, COMPANION_EMOTIONS, portraitAssetId, type CompanionEmotion } from "@nano/content";
import type { AvatarId, Equipment } from "@nano/contracts";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { bridge } from "../../app/bridge.ts";
import { AURA_FEET_Y, auraParticles } from "../../game/avatar/motion.ts";
import { layoutWear, type WearLayout, type WearPiece } from "../../game/avatar/rig.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { COMPANION_LINES, companionVi } from "../../i18n/companion.vi.ts";
import { assetEntry, imageUrl } from "../../services/assets/assets.ts";
import { playVoice, type VoiceMood } from "../../services/audio/voice.ts";
import { clip, pieceTransform, useLiveWear, useParticleArt } from "../components/AvatarPreview.tsx";
import { Art } from "../components/common.tsx";
import "../../styles/companion.css";
import { COMPANION_GAP, COMPANION_HEADROOM, COMPANION_HEADROOM_MAX, companionHeight } from "./layout.ts";
import { initialCompanion, react, type CompanionEvent, type CompanionState, type LinePool } from "./machine.ts";

const TICK_MS = 400;
/** The Color Lab is a quiet, thinking house: the companion speaks half as often there. */
const LAB_CHATTER = 0.5;
const MOOD: Record<LinePool, VoiceMood> = {
  start: "bright",
  combo: "bright",
  bin: "bright",
  comeback: "bright",
  win3: "bright",
  win: "bright",
  poke: "bright",
  wake: "curious",
  phase: "curious",
  undo: "curious",
  hint: "curious",
  oops: "soft",
  oopsAgain: "soft",
  cry: "soft",
  sleepy: "sleepy",
};
const HIGHER_VOICES: ReadonlySet<string> = new Set(["princess", "sorceress", "angel-girl", "demon-girl"]);

function pickLine(pool: LinePool, last: string | null): string {
  const lines = COMPANION_LINES[pool];
  const fresh = lines.filter((line) => line !== last);
  const from = fresh.length > 0 ? fresh : lines;
  return from[Math.floor(Math.random() * from.length)] ?? "";
}

/** Aura particles hug the bust: they are sized for a figure this many bust heights tall. */
const AURA_SPAN = 1.15;

/**
 * Worn items and auras on the bust, with the city avatar's own rig and
 * motion: everything is laid out on a virtual full body placed so its head
 * coincides with the bust's (see `bustBodyBox`). Wings flap, capes sway and
 * aura particles drift exactly as on the walking character; anything that
 * reaches below the bust is cut at its bottom edge by the figure's clip.
 */
function bustWear(avatarId: AvatarId, equipped: Equipment, width: number, height: number): { box: ReturnType<typeof bustBodyBox>; layout: WearLayout; headroom: number } {
  const box = bustBodyBox(avatarId, width, height);
  const layout = layoutWear(avatarId, "front", equipped, { width: box.width, height: box.height }, (assetId) => {
    const entry = assetEntry(assetId);
    return entry ? { width: entry.width, height: entry.height } : undefined;
  });
  // Room above the bust for the tallest thing worn (a witch hat, bunny ears, a staff tip).
  const top = Math.min(0, ...layout.pieces.map((p) => box.y + p.y - p.height * p.originY));
  const headroom = Math.min(COMPANION_HEADROOM_MAX, Math.max(COMPANION_HEADROOM, -top / height + 0.03)) * height;
  return { box, layout, headroom };
}

function BustRig({ wear, width, height, children }: { wear: ReturnType<typeof bustWear>; width: number; height: number; children: ReactNode }) {
  const rigRef = useRef<HTMLDivElement>(null);
  const { box, layout } = wear;
  const auraSize = height * AURA_SPAN;
  const particleUrls = useParticleArt(layout.aura?.itemId, layout.aura?.fx);
  useLiveWear(rigRef, layout, auraSize, false, particleUrls);
  const aura = layout.aura;
  const auraAt = { left: -box.x + width / 2, top: -box.y + height * 0.55 };
  const piece = (p: WearPiece, i: number) => (
    <span
      key={`${p.slot}-${i}`}
      className="avatar-preview__item"
      data-piece={p.motion ? i : undefined}
      style={{
        left: p.x - p.width * p.originX,
        top: p.y - p.height * p.originY,
        width: p.width,
        height: p.height,
        zIndex: p.layer === "back" ? 0 : 2,
        transformOrigin: `${p.originX * 100}% ${p.originY * 100}%`,
        transform: pieceTransform(p),
        clipPath: p.crop ? clip(p.crop) : undefined,
      }}
    >
      <Art id={p.assetId} />
    </span>
  );
  return (
    <div ref={rigRef} className="companion__rig" style={{ left: box.x, top: box.y, width: box.width, height: box.height }}>
      {aura ? (
        <div className="avatar-preview__aura" style={auraAt}>
          <span
            className="avatar-preview__glow"
            data-glow
            style={{
              top: AURA_FEET_Y * auraSize,
              width: auraSize * 0.62,
              height: auraSize * 0.16,
              background: `radial-gradient(closest-side, #${aura.fx.glow.toString(16).padStart(6, "0")}, transparent)`,
            }}
          />
        </div>
      ) : null}
      {aura && particleUrls
        ? auraParticles(aura.fx, 0).map((_, i) => <span key={`fx-${i}`} className="avatar-preview__particle" data-particle={i} style={auraAt} />)
        : null}
      {layout.pieces.map((p, i) => (p.layer === "back" ? piece(p, i) : null))}
      <div className="companion__faces" style={{ left: -box.x, top: -box.y, width, height }}>
        {children}
      </div>
      {layout.pieces.map((p, i) => (p.layer === "front" ? piece(p, i) : null))}
    </div>
  );
}

/**
 * The player's own character, watching from the bottom-left corner of the
 * puzzle screen and reacting to play (see machine.ts). Puzzle scenes keep
 * the strip it sits in free via the `layout:bottom-inset` bridge event.
 */
export function Companion() {
  const { avatarId, equipped, enabled, result, frozen, lab } = useApp(
    (s) => ({
      avatarId: s.save?.profile.avatarId ?? null,
      equipped: s.ui.previewEquip ?? s.save?.equipped ?? null,
      enabled: s.save?.settings.companion !== false,
      result: s.ui.result !== null,
      frozen: s.ui.paused || s.ui.tutorial !== null || s.ui.outOfMoves,
      // The Color Lab, the Logic Tower and the Magic Workshop keep a calmer companion.
      lab: s.ui.puzzle?.houseId === "colors" || s.ui.puzzle?.houseId === "logic" || s.ui.puzzle?.houseId === "fill",
    }),
    shallowEqual,
  );
  const showingResult = result;
  const [view, setView] = useState<{ emotion: CompanionEmotion; line: string | null; lineKey: number; leaving: boolean }>({
    emotion: "calm",
    line: null,
    lineKey: 0,
    leaving: false,
  });
  const [height, setHeight] = useState(() => companionHeight(typeof window === "undefined" ? 640 : window.innerHeight));
  const [onCard, setOnCard] = useState<{ left: number; bottom: number } | "hidden" | null>(null);
  const machine = useRef<CompanionState>(initialCompanion(Date.now()));
  const lastLine = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const active = enabled && avatarId !== null;

  const dispatch = useCallback(
    (event: CompanionEvent) => {
      const before = machine.current;
      const after = react(before, event, Date.now(), Math.random, { chatter: lab ? LAB_CHATTER : 1 });
      machine.current = after;
      const spoke = after.speech && after.speech.serial !== before.speech?.serial ? after.speech : null;
      if (spoke) {
        const line = pickLine(spoke.pool, lastLine.current);
        lastLine.current = line;
        playVoice(MOOD[spoke.pool], avatarId && HIGHER_VOICES.has(avatarId) ? 3 : 0);
        setView((current) => ({ emotion: after.emotion, line, lineKey: current.lineKey + 1, leaving: false }));
      } else if (after.emotion !== before.emotion) {
        setView((current) => ({ ...current, emotion: after.emotion }));
      }
    },
    [avatarId, lab],
  );

  // Bridge events from whichever house scene is running.
  useEffect(() => {
    if (!active) return;
    const offs = [
      bridge.on("puzzle:ready", () => dispatch({ type: "start" })),
      bridge.on("puzzle:place", ({ combo, binFull }) => dispatch({ type: "place", combo, binFull })),
      bridge.on("puzzle:won", ({ stars }) => dispatch({ type: "win", stars })),
      bridge.on("puzzle:out-of-moves", () => dispatch({ type: "mistake" })),
      bridge.on("puzzle:feedback", (kind) => {
        if (kind === "mistake" || kind === "undo" || kind === "hint" || kind === "phase" || kind === "pick") dispatch({ type: kind });
      }),
      bridge.on("puzzle:command", (command) => {
        if (command !== "restart") return;
        machine.current = initialCompanion(Date.now());
        setView((current) => ({ ...current, emotion: "calm", line: null }));
      }),
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [active, dispatch]);

  // Time passes only while the player can actually play.
  useEffect(() => {
    if (!active || frozen || showingResult) return;
    const timer = setInterval(() => dispatch({ type: "tick" }), TICK_MS);
    return () => clearInterval(timer);
  }, [active, frozen, showingResult, dispatch]);

  // Let the speech bubble read for a moment, then fade it out.
  useEffect(() => {
    if (!view.line) return;
    const key = view.lineKey;
    const fade = setTimeout(() => setView((current) => (current.lineKey === key ? { ...current, leaving: true } : current)), Math.min(4600, 2400 + view.line.length * 35));
    const gone = setTimeout(() => setView((current) => (current.lineKey === key ? { ...current, line: null } : current)), Math.min(4600, 2400 + view.line.length * 35) + 280);
    return () => {
      clearTimeout(fade);
      clearTimeout(gone);
    };
  }, [view.lineKey, view.line]);

  const sample = avatarId ? assetEntry(portraitAssetId(avatarId, "calm")) : undefined;
  const width = sample ? (height * sample.width) / sample.height : 0;
  const wear = avatarId && equipped && sample ? bustWear(avatarId, equipped, width, height) : null;
  const headroomKey = Math.round(wear?.headroom ?? 0);

  // Tell puzzle scenes how much room to keep free at the bottom (hats change the headroom).
  useLayoutEffect(() => {
    const measure = () => {
      setHeight(companionHeight(window.innerHeight));
      const bust = rootRef.current?.querySelector(".companion__bust")?.getBoundingClientRect();
      const app = rootRef.current?.closest(".app")?.getBoundingClientRect();
      bridge.emit("layout:bottom-inset", active && bust && app ? Math.ceil(app.bottom - bust.top + COMPANION_GAP) : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
      bridge.emit("layout:bottom-inset", 0);
    };
  }, [active, height, headroomKey]);

  // On the result card the companion perches on the card's corner (or steps aside).
  useEffect(() => {
    if (!showingResult) {
      setOnCard(null);
      return;
    }
    const place = () => {
      const card = document.querySelector(".result__card")?.getBoundingClientRect();
      const bust = rootRef.current?.querySelector(".companion__bust")?.getBoundingClientRect();
      if (!card || !bust) return;
      const clear = bust.top >= card.bottom - 4 || bust.right <= card.left + 4;
      if (clear) setOnCard(null);
      else if (card.top >= height * 0.8 + 8) setOnCard({ left: Math.max(8, card.left + 6), bottom: window.innerHeight - card.top - 16 });
      else setOnCard("hidden");
    };
    const timer = setTimeout(place, 320);
    window.addEventListener("resize", place);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", place);
    };
  }, [showingResult, height]);

  // No usable bust art (e.g. a release build before art approval): stay out of the way.
  if (!active || !avatarId || !wear) return null;
  const headroom = wear.headroom;
  const perch = onCard && onCard !== "hidden" ? { left: onCard.left, bottom: onCard.bottom } : undefined;
  return (
    <div
      ref={rootRef}
      className={`companion companion--${view.emotion} ${showingResult ? "companion--result" : ""} ${onCard === "hidden" ? "companion--away" : ""}`}
      style={perch}
      data-testid="companion"
      data-emotion={view.emotion}
    >
      <button
        type="button"
        className="companion__bust"
        style={{ width, height: height + headroom }}
        aria-label={companionVi.pokeLabel}
        onPointerDown={() => dispatch({ type: "poke" })}
      >
        <span className="companion__halo" style={{ height: height * 0.58 }} aria-hidden="true" />
        {/*
          Items may rise into the headroom and spread sideways, never below the bust. A real
          overflow box (not clip-path) also keeps them out of the page's scrollable area, so
          focusing a button can never scroll the whole game.
        */}
        <span className="companion__clip" style={{ left: -width * 0.6, width: width * 2.2, height: height + headroom }}>
          <span className="companion__figure" style={{ left: width * 0.6, top: headroom, width, height }}>
            <BustRig wear={wear} width={width} height={height}>
              {COMPANION_EMOTIONS.map((emotion) => {
                const src = imageUrl(portraitAssetId(avatarId, emotion));
                return src ? (
                  <img
                    key={emotion}
                    className={`companion__face ${emotion === view.emotion ? "companion__face--on" : ""}`}
                    src={src}
                    alt=""
                    draggable={false}
                  />
                ) : null;
              })}
            </BustRig>
          </span>
        </span>
      </button>
      {view.line ? (
        <p className={`companion__bubble ${view.leaving ? "companion__bubble--out" : ""}`} key={view.lineKey} role="status" data-testid="companion-bubble">
          {view.line}
        </p>
      ) : null}
    </div>
  );
}
